import React from 'react'
// NOTE: re-add Zap to this import when un-commenting the Optimizer button below.
import { Play } from 'lucide-react'
import { Button, ChartContextChip, ChartContextDialog, ChatInput, ChatMessageUpgrade } from '@/components'
import type { ChatInputHandle, UserMessageAttachment } from '@/components'
import { chartPush } from '../chartPush'
import ClarifyOverlay from './ClarifyOverlay'
import { CLARIFY_TOOL_NAME } from './clarifyTypes'
import type { ClarifyData } from './clarifyTypes'
import BotMessageUser from './BotMessageUser'
import BotMessageAssistant from './BotMessageAssistant'
import BotMessageToolForm from './BotMessageToolForm'
import BotMessageToolGenerateCode from './BotMessageToolGenerateCode'
import BotMessageToolBacktest from './BotMessageToolBacktest'
import BotMessageToolOptimizer from './BotMessageToolOptimizer'
import { MESSAGE_STATUS } from '../../contexts/SSEEvents'
import type { MessageStatus } from '../api'

// Ready-to-send message seeded when a chart is attached from the workbench —
// the user shouldn't have to type; Enter alone sends. Never overwrites text
// already sitting in the composer.
const CHART_PUSH_PREFILL =
  "Here's a chart with my entry and exit plotted. Please write the code for it - or ask me questions if you need to."

/** What an upgrade card's button should do. */
export type UpgradeAction = 'billing' | 'paywall'

export interface Message {
  id: string
  role: 'user' | 'assistant' | 'upgrade'
  content: string
  timestamp: Date
  status?: MessageStatus
  streaming?: boolean
  /** Upgrade cards only: overrides the default CTA label and destination. */
  upgradeAction?: UpgradeAction
  ctaLabel?: string
  attachments?: UserMessageAttachment[]
  messageType?: 'content' | 'tool'
  toolData?: {
    toolName: string
    toolInput: Record<string, unknown>
    startedAt: string
    completedAt?: string
    status: 'running' | 'completed'
    componentType?: string
    componentData?: Record<string, unknown>
    responses?: Record<string, string | string[]>
    dismissed?: boolean
    toolStatus?: string
    config?: Record<string, any>
    btid?: string
    cid?: string
    optid?: string
    version?: number
    codeVersion?: number
    codeId?: number
    error?: string
  }
  error?: string
}

interface BotChatProps {
  messages: Message[]
  /**
   * `intent` marks a send that exists only to start a run, so the caller can
   * refuse it up front on a plan that cannot run them — no chat turn spent.
   */
  onSendMessage: (value: string, opts?: { intent?: 'backtest' | 'optimizer' }) => void
  onComponentSubmit: (msgId: string, data: Record<string, unknown>) => void
  onComponentDismiss: (msgId: string) => void
  onCancelMessage?: () => void
  onRetryMessage?: () => void
  onUpgradeClick?: (action?: UpgradeAction) => void
  /** "Run optimizer" on a completed-backtest badge. */
  onRunOptimizer?: () => void
  /** "View full report" on a completed-backtest badge — opens the Backtest tab. */
  onViewReport?: () => void
  isLoading?: boolean
  hasCode?: boolean
  hideHeader?: boolean
  isMobileLayout?: boolean
  inputTrailingSlot?: React.ReactNode
  /** Reasoning-effort control for the composer chrome row. */
  effortSlot?: React.ReactNode
  /** Show the "Run Backtest" quick action. */
  showBacktestAction?: boolean
  /** Chart context staged on the composer; sent with the next message. */
  stagedAttachment?: { label: string; text: string } | null
  onRemoveStagedAttachment?: () => void
  /** User edited the staged context text in the review dialog. */
  onUpdateStagedAttachment?: (text: string) => void
}

export default function BotChat({
  messages,
  onSendMessage,
  onComponentSubmit,
  onComponentDismiss,
  onCancelMessage,
  onRetryMessage,
  onUpgradeClick,
  onRunOptimizer,
  onViewReport,
  isLoading = false,
  hasCode = false,
  hideHeader = false,
  isMobileLayout = false,
  inputTrailingSlot,
  effortSlot,
  showBacktestAction = false,
  stagedAttachment,
  onRemoveStagedAttachment,
  onUpdateStagedAttachment,
}: BotChatProps) {
  const messagesContainerRef = React.useRef<HTMLDivElement>(null)
  const feedRef = React.useRef<HTMLDivElement>(null)
  const prevMessagesLengthRef = React.useRef(0)
  const hasScrolledOnLoadRef = React.useRef(false)
  const [showScrollButton, setShowScrollButton] = React.useState(false)
  // Review/edit dialog for the staged chart context (opened from the chip).
  const [ctxDialogOpen, setCtxDialogOpen] = React.useState(false)
  const chatInputRef = React.useRef<ChatInputHandle>(null)

  // "Add to AI chat" on the workbench staged a chart context — seed the
  // composer with a ready-to-send message (only when it is empty). Keyed on
  // chip PRESENCE, not identity: it must fire when the chat mounts with a
  // chip already staged (mobile sheet opens after the stage event) and when
  // a persisted chip is re-seeded after a reload, but not on every re-render
  // while a chip sits on the composer.
  const hasStagedAttachment = !!stagedAttachment
  React.useEffect(() => {
    if (hasStagedAttachment) chatInputRef.current?.prefillIfEmpty(CHART_PUSH_PREFILL)
    return chartPush.onStage(() => chatInputRef.current?.prefillIfEmpty(CHART_PUSH_PREFILL))
  }, [hasStagedAttachment])
  // Check if there's any in_progress message (blocks input)
  const hasInProgressMessage = React.useMemo(() => {
    return messages.some(m => m.status === MESSAGE_STATUS.IN_PROGRESS)
  }, [messages])

  // Check if there's a failed message (blocks input until resolved)
  const failedMessage = React.useMemo(() => {
    return messages.find(m => m.status === MESSAGE_STATUS.FAILED)
  }, [messages])

  // Pending clarifying-questions form: the stepper overlay replaces the
  // composer while it's unanswered. Keyed off message.status (not
  // toolData.status, which is 'running' via SSE but 'waiting_user' from DB
  // load). The questions guard keeps the plain composer during the instant
  // between tool-started and type-changed when componentData isn't set yet.
  const pendingQuestionsMessage = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (
        m.status === MESSAGE_STATUS.IN_PROGRESS &&
        m.toolData?.toolName === CLARIFY_TOOL_NAME &&
        ((m.toolData.componentData as ClarifyData | undefined)?.questions?.length ?? 0) > 0
      ) {
        return m
      }
    }
    return null
  }, [messages])

  // Check if there's an active tool call (to suppress assistant spinner)
  const hasActiveToolCall = React.useMemo(() => {
    return messages.some(m => m.messageType === 'tool' && m.toolData && m.toolData.status === 'running')
  }, [messages])

  // Input should be disabled if: loading, in_progress message, or failed message
  const isInputDisabled = isLoading || hasInProgressMessage || !!failedMessage

  // Scroll to bottom on initial load if there are messages
  React.useEffect(() => {
    if (!hasScrolledOnLoadRef.current && messages.length > 0 && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
      hasScrolledOnLoadRef.current = true
    }
  }, [messages])

  // Scroll when new messages are appended: reveal the beginning of the new
  // content if the user was at the bottom (or sent a message themselves);
  // leave the viewport alone when they are reading older messages. Streaming
  // chunks don't grow the count, so a long reply never drags the view down
  // while it streams in. Several messages can land in one render (the server
  // emits message-user and message-started back-to-back), so the whole
  // appended batch is considered, anchored on its first message.
  React.useEffect(() => {
    const prevLength = prevMessagesLengthRef.current
    prevMessagesLengthRef.current = messages.length
    // prevLength === 0 is the initial population (or a bot switch) — the
    // load effect above owns positioning there.
    if (prevLength === 0 || messages.length <= prevLength) return

    const isOwnSend = messages.slice(prevLength).some((m) => m.role === 'user')
    requestAnimationFrame(() => {
      const container = messagesContainerRef.current
      const wrappers = feedRef.current?.children
      const firstNew = wrappers?.[prevLength] as HTMLElement | undefined
      if (!container || !wrappers || !firstNew) return

      // The DOM already contains the appended batch — discount what it added
      // (heights + the space-y-4 gaps) to know where the user stood before
      // it landed. 100px slack mirrors handleScroll's isAtBottom.
      let addedHeight = 0
      for (let i = prevLength; i < wrappers.length; i++) {
        addedHeight += (wrappers[i] as HTMLElement).offsetHeight + 16
      }
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight
      const wasNearBottom = distanceFromBottom - addedHeight < 100

      if (isOwnSend || wasNearBottom) {
        // offsetTop is relative to the container (its offsetParent): tall new
        // content gets its beginning aligned to the top; short content clamps
        // to a plain scroll-to-bottom.
        container.scrollTo({
          top: Math.max(0, firstNew.offsetTop - 8),
          behavior: 'smooth',
        })
      }
    })
  }, [messages])

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 100
      setShowScrollButton(!isAtBottom)
    }
  }

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }
  }

  const placeholder = failedMessage
    ? 'Resolve the error above to continue...'
    : hasInProgressMessage
      ? 'Waiting for response...'
      : 'Type a message...'

  const primaryAction: 'send' | 'stop' =
    hasInProgressMessage && onCancelMessage ? 'stop' : 'send'

  // Banner row above the input: the "run a backtest" quick action (enabled
  // once nothing is running AND strategy code exists) and the staged
  // chart-context chip. Omitted entirely when both are absent.
  const quickActionsDisabled = isInputDisabled || !hasCode
  const composerBanner =
    showBacktestAction || stagedAttachment ? (
      <div className="flex items-center gap-1.5 pb-1.5">
        {showBacktestAction && (
          <Button
            type="button"
            variant={quickActionsDisabled ? 'outline' : 'default'}
            size="xs"
            disabled={quickActionsDisabled}
            onClick={() => onSendMessage('run a backtest', { intent: 'backtest' })}
            title={!hasCode ? 'Generate strategy code first' : undefined}
          >
            <Play /> Run Backtest
          </Button>
        )}
        {/* <Button
          type="button"
          variant="outline"
          size="xs"
          disabled={quickActionsDisabled}
          onClick={() => onSendMessage('run the parameter optimizer')}
          title={!hasCode ? 'Generate strategy code first' : undefined}
        >
          <Zap /> Optimizer
        </Button> */}
        {stagedAttachment && (
          <div id="chat-attach-chip" className="flex flex-wrap gap-1.5">
            <ChartContextChip
              label={stagedAttachment.label}
              onClick={() => setCtxDialogOpen(true)}
              onRemove={onRemoveStagedAttachment}
            />
          </div>
        )}
      </div>
    ) : undefined

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden bg-background">
      {!hideHeader && (
        <div className="shrink-0 h-11 flex items-center px-4 border-b border-border bg-card">
          <div className="flex items-center gap-1.5">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-foreground shrink-0"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <p className="text-label text-foreground">Chat</p>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 relative">
        <div
          ref={messagesContainerRef}
          className="absolute inset-0 overflow-y-auto overflow-x-hidden p-4"
          onScroll={handleScroll}
        >
          {messages.length === 0 ? (
            <div className="text-center mt-8 text-muted-foreground">
              Start a conversation about your strategy
            </div>
          ) : (
            <div ref={feedRef} className="space-y-4">
              {messages.map((message) => {
                return (
                  <div key={message.id}>
                    {message.role === 'upgrade' ? (
                      <ChatMessageUpgrade
                        message={{ id: 0, type: 'upgrade', content: message.content }}
                        ctaLabel={message.ctaLabel ?? 'Check credit usage'}
                        onUpgrade={() => onUpgradeClick?.(message.upgradeAction)}
                      />
                    ) : message.role === 'user' ? (
                      <BotMessageUser
                        content={message.content}
                        status={message.status}
                        attachments={message.attachments}
                      />
                    ) : message.role === 'assistant' ? (
                      message.messageType === 'tool' || message.toolData ? (
                        message.toolData?.toolName === CLARIFY_TOOL_NAME ? (
                          <BotMessageToolForm
                            toolData={message.toolData}
                            status={message.status}
                          />
                        ) : message.toolData?.toolName === 'generate_trading_code' ? (
                          <BotMessageToolGenerateCode
                            toolData={message.toolData}
                            status={message.status}
                          />
                        ) : message.toolData?.toolName === 'run_backtest' ? (
                          <BotMessageToolBacktest
                            toolData={message.toolData}
                            status={message.status}
                            msgId={message.id}
                            onRunOptimizer={onRunOptimizer}
                            onViewReport={onViewReport}
                            actionsDisabled={isInputDisabled}
                          />
                        ) : message.toolData?.toolName === 'run_optimizer' ? (
                          <BotMessageToolOptimizer
                            toolData={message.toolData}
                            status={message.status}
                          />
                        ) : null
                      ) : (
                        <BotMessageAssistant
                          content={message.content}
                          status={message.status}
                          streaming={message.streaming}
                          error={message.error}
                          onRetry={onRetryMessage}
                          onCancel={onCancelMessage}
                          hasActiveToolCall={hasActiveToolCall}
                        />
                      )
                    ) : null}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {showScrollButton && (
          <button
            type="button"
            onClick={scrollToBottom}
            className="absolute left-1/2 -translate-x-1/2 bottom-3 z-10 px-3 py-1.5 rounded-full text-caption bg-card text-foreground border border-border shadow-md"
            aria-label="Scroll to bottom"
          >
            ↓
          </button>
        )}
      </div>

      <ChatInput
        ref={chatInputRef}
        id="chat-composer"
        onSubmit={(text) => onSendMessage(text)}
        onStop={onCancelMessage ? () => onCancelMessage() : undefined}
        primaryAction={primaryAction}
        disabled={isInputDisabled && primaryAction !== 'stop'}
        placeholder={placeholder}
        isMobileLayout={isMobileLayout}
        bannerSlot={composerBanner}
        trailingChromeSlot={inputTrailingSlot}
        leadingChromeSlot={effortSlot}
        overlaySlot={
          pendingQuestionsMessage?.toolData ? (
            <ClarifyOverlay
              // Keyed by message id so a new questions form resets the stepper
              key={pendingQuestionsMessage.id}
              msgId={pendingQuestionsMessage.id}
              data={pendingQuestionsMessage.toolData.componentData as unknown as ClarifyData}
              onSubmit={onComponentSubmit}
              onDismiss={onComponentDismiss}
            />
          ) : undefined
        }
      />

      {stagedAttachment && (
        <ChartContextDialog
          open={ctxDialogOpen}
          onOpenChange={setCtxDialogOpen}
          label={stagedAttachment.label}
          text={stagedAttachment.text}
          onSave={onUpdateStagedAttachment}
        />
      )}
    </div>
  )
}

export type { BotChatProps }
